import React, { useState } from 'react';
import { t } from '../i18n';
import './DateRangeSelector.css';

/**
 * DateRangeSelector component allows users to select custom begin and end dates
 * 
 * Requirements:
 * - 2.1: Display Date_Range_Selector component
 * - 2.2: Provide begin date input field
 * - 2.3: Provide end date input field
 * - 2.4: Fetch statistics when both dates are selected
 * - 2.5: Display validation error when end date is before begin date
 * 
 * @param {Object} props
 * @param {Function} props.onDateRangeChange - Callback when both dates are valid (beginDate, endDate)
 */
function DateRangeSelector({ onDateRangeChange, value, onChange, showTitle = true }) {
  const [internalRange, setInternalRange] = useState({ beginDate: '', endDate: '' });
  const { beginDate, endDate } = value || internalRange;
  const validationError = beginDate && endDate && endDate < beginDate
    ? t('accountActivity.calendar.invalidRange') : null;

  /**
   * Validate date range and call callback if valid
   * Requirements 2.4, 2.5
   */
  const validateAndNotify = (newBeginDate, newEndDate) => {
    // Check if both dates are provided
    if (!newBeginDate || !newEndDate) {
      return;
    }

    // Validate that end date is not before begin date (Requirement 2.5)
    if (newEndDate < newBeginDate) {
      return;
    }

    // Both dates are valid, call callback (Requirement 2.4)
    onDateRangeChange?.(newBeginDate, newEndDate);
  };

  const handleBeginDateChange = (e) => {
    const newBeginDate = e.target.value;
    const next = { beginDate: newBeginDate, endDate };
    setInternalRange(next);
    onChange?.(next);
    validateAndNotify(newBeginDate, endDate);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    const next = { beginDate, endDate: newEndDate };
    setInternalRange(next);
    onChange?.(next);
    validateAndNotify(beginDate, newEndDate);
  };

  return (
    <div className="date-range-selector">
      {showTitle && <h3 className="date-range-title">{t('accountActivity.calendar.customRange')}</h3>}
      <div className="date-range-inputs">
        <div className="date-input-group">
          <label htmlFor="begin-date" className="date-label">
            {t('accountActivity.calendar.beginDate')}
          </label>
          <input
            id="begin-date"
            type="date"
            className="date-input"
            value={beginDate}
            onChange={handleBeginDateChange}
            aria-label={t('accountActivity.calendar.beginDateLabel')}
          />
        </div>
        <div className="date-input-group">
          <label htmlFor="end-date" className="date-label">
            {t('accountActivity.calendar.endDate')}
          </label>
          <input
            id="end-date"
            type="date"
            className="date-input"
            value={endDate}
            onChange={handleEndDateChange}
            aria-invalid={Boolean(validationError)}
            aria-label={t('accountActivity.calendar.endDateLabel')}
          />
        </div>
      </div>
      {validationError && (
        <div className="validation-error" role="alert">
          {validationError}
        </div>
      )}
    </div>
  );
}

export default DateRangeSelector;
